import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  VStack,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

const ResetPasswordForm = ({ onSubmit, token }) => {
  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Error state
  const [errors, setErrors] = useState({
    password: '',
    confirmPassword: '',
    form: ''
  });
  
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate form inputs
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      password: '',
      confirmPassword: '',
      form: ''
    };
    
    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      isValid = false;
    }
    
    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  // Get auth context
  const { resetPassword } = useAuth();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Reset password using Firebase
      const result = await resetPassword(token, formData.password);
      
      if (result.success) {
        // Show success message
        setIsSubmitted(true);
        
        // Call the onSubmit prop with form data if provided
        if (onSubmit) {
          await onSubmit({
            password: formData.password,
            token,
            success: true
          });
        }
      } else {
        // Handle error
        setErrors(prev => ({ 
          ...prev, 
          form: result.error || 'Failed to reset password. Please try again.' 
        }));
      }
      
    } catch (error) {
      // Handle error
      setErrors(prev => ({ 
        ...prev, 
        form: error.message || 'An error occurred. Please try again.' 
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={bgColor}
      p={8}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      maxW="400px"
    >
      <VStack spacing={4} align="flex-start" width="100%">
        <Text fontSize="2xl" fontWeight="bold">Create New Password</Text>
        
        {isSubmitted ? (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            Your password has been reset successfully.
          </Alert>
        ) : (
          <>
            <Text fontSize="sm">
              Please create a new password for your account.
            </Text>
            
            {errors.form && (
              <Box 
                p={3} 
                bg="red.100" 
                color="red.800" 
                width="100%" 
                borderRadius="md"
              >
                {errors.form}
              </Box>
            )}
            
            {/* Password Field */}
            <FormControl isInvalid={!!errors.password}>
              <FormLabel htmlFor="password">New Password</FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
            
            {/* Confirm Password Field */}
            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
              <InputGroup>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>
            
            <Button
              type="submit"
              colorScheme="teal"
              width="100%"
              mt={4}
              isLoading={isLoading}
              loadingText="Resetting Password"
            >
              Reset Password
            </Button>
          </>
        )}
        
        {isSubmitted && (
          <Box textAlign="center" width="100%" mt={4}>
            <Button 
              variant="link" 
              colorScheme="blue" 
              size="sm" 
              as="a" 
              href="/login"
            >
              Back to Login
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default ResetPasswordForm;
